export type WithPreferred<T> = T & { isPreferred: boolean };
export type WithPreferredFlag<T> = T & { preferredFlg: FlagMarker };

export interface BaseMemberFormData {
  groupId?: number;
  firstNm: string;
  middleNm?: string;
  lastNm: string;
  suffix?: string;
  dob: string;
  genderCd: Gender;
  clientId: string;
  effectiveStartDt: string;
  primaryFlg?: string | boolean;
  readOnly?: boolean;
  isMailingAddressDifferent: boolean;
}

export interface MemberFormData extends BaseMemberFormData {
  group: Group;
  mailingAddress: BaseUIAddress;
  homeAddress: BaseUIAddress;
  phone1: BaseUIPhone;
  phone2: BaseUIPhone;
  languageCd?: string;
  contactEmail: string;
  formConfig: object;
  phoneIsPreferred: 'phone1' | 'phone2';
}

export interface BaseUIPhone {
  areaCode?: string;
  phoneNumber: string;
  phoneFaxTypeCd: PhoneFaxType;
  isPreferred?: boolean;
  extension?: string;
}

export interface BaseUIAddress {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  stateProvince: string;
  postal: string;
  isPreferred: boolean;
}

export interface MemberToggleStatusData {
  memberId: string;
  action: MemberToggleStatusAction;
  effectiveEndDt?: string;
}

export interface MemberDetailsFormData extends BaseMemberFormData {
  mailingAddress?: WithPreferred<Address>;
  homeAddress?: WithPreferred<Address>;
  phone1?: WithPreferredFlag<Phone>;
  phone2?: WithPreferredFlag<Phone>;
  createdAt?: string;
  group: Group;
  effectiveEndDt?: string;
  relatedInstances?: MemberRelatedInstance[];
  originMember: MemberDetails;
  contactEmail?: string;
  languageCd?: string;
  relatedInstancesPresent: boolean;
  couldBeReactivated?: boolean;
  phoneIsPreferred?: string;
  isPrimaryInactive?: boolean;
  relationshipSubtypeCd?: RelationshipSubType;
}

export enum MemberStatus {
  MEMBERSTATUS_ACT = 'MEMBERSTATUS_ACT',
  MEMBERSTATUS_INA = 'MEMBERSTATUS_INA',
  MEMBERSTATUS_PEN = 'MEMBERSTATUS_PEN'
}

export enum RelationshipStatus {
  RELATIONSHIPSTATUS_CONFIRMED = 'RELATIONSHIPSTATUS_CONFIRMED',
  RELATIONSHIPSTATUS_DENIED = 'RELATIONSHIPSTATUS_DENIED',
  RELATIONSHIPSTATUS_PENDING = 'RELATIONSHIPSTATUS_PENDING',
  RELATIONSHIPSTATUS_REVOKED = 'RELATIONSHIPSTATUS_REVOKED'
}

export enum FlagMarker {
  Y = 'Y',
  N = 'N'
}

export enum Gender {
  GENDER_MALE = 'GENDER_MALE',
  GENDER_FEMALE = 'GENDER_FEMALE',
  GENDER_OTHER = 'GENDER_OTHER',
  GENDER_UNKNOWN = 'GENDER_UNKNOWN'
}

export enum ExclusionCode {
  IN = 'IN',
  EX = 'EX'
}

export enum AddressType {
  ADDRESSTYPE_HOME = 'ADDRESSTYPE_HOME',
  ADDRESSTYPE_WORK = 'ADDRESSTYPE_WORK',
  ADDRESSTYPE_MAIL = 'ADDRESSTYPE_MAIL',
  ADDRESSTYPE_HQ = 'ADDRESSTYPE_HQ',
  ADDRESSTYPE_ALT = 'ADDRESSTYPE_ALT',
  ADDRESSTYPE_BILL = 'ADDRESSTYPE_BILL',
  ADDRESSTYPE_RENDERING = 'ADDRESSTYPE_RENDERING'
}

export enum PhoneFaxType {
  PHONEFAXTYPE_WORK = 'PHONEFAXTYPE_WORK',
  PHONEFAXTYPE_WORKFAX = 'PHONEFAXTYPE_WORKFAX',
  PHONEFAXTYPE_HOME = 'PHONEFAXTYPE_HOME',
  PHONEFAXTYPE_HOMEFAX = 'PHONEFAXTYPE_HOMEFAX',
  PHONEFAXTYPE_CELL = 'PHONEFAXTYPE_CELL'
}
export enum EmailType {
  EMAILTYPE_ALERT = 'EMAILTYPE_ALERT',
  EMAILTYPE_HOME = 'EMAILTYPE_HOME',
  EMAILTYPE_WORK = 'EMAILTYPE_WORK'
}
export enum RelationshipType {
  RELATIONSHIPTYPE_CAREGIVER = 'RELATIONSHIPTYPE_CAREGIVER',
  RELATIONSHIPTYPE_DEPENDENT = 'RELATIONSHIPTYPE_DEPENDENT',
  RELATIONSHIPTYPE_OTHERRELATIONSHIP = 'RELATIONSHIPTYPE_OTHERRELATIONSHIP'
}
export enum RelationshipSubType {
  RELATIONTYPE_AUTHC = 'RELATIONTYPE_AUTHC', // Authorized Consenter
  RELATIONTYPE_BUSINESSPARTNER = 'RELATIONTYPE_BUSINESSPARTNER', // Business Partner
  RELATIONTYPE_DAU = 'RELATIONTYPE_DAU', // Daughter
  RELATIONTYPE_DECISION = 'RELATIONTYPE_DECISION', // Decision Maker
  RELATIONTYPE_DOM = 'RELATIONTYPE_DOM', // Domestic Partner
  RELATIONTYPE_EMR = 'RELATIONTYPE_EMR', // Emergency Contact
  RELATIONTYPE_HUS = 'RELATIONTYPE_HUS', // Husband
  RELATIONTYPE_OTH = 'RELATIONTYPE_OTH', // Other
  RELATIONTYPE_PAR = 'RELATIONTYPE_PAR', // Parent
  RELATIONTYPE_PRI = 'RELATIONTYPE_PRI', // Primary
  RELATIONTYPE_SON = 'RELATIONTYPE_SON', // Son
  RELATIONTYPE_UNK = 'RELATIONTYPE_UNK', // Unknown
  RELATIONTYPE_WIFE = 'RELATIONTYPE_WIFE' // Wife
}

export interface BaseMemberMetaData {
  memberMasterMrnId?: string;
  firstNm: string;
  lastNm: string;
  middleNm?: string;
  suffix?: string;
  sourceMrnRoot?: string;
  individualId?: string;
  sourceMrnId: string;
  dataSourceCd?: string;
  namespace?: string;
  primaryFlg: FlagMarker;
  mrnSourceClassificationCd?: string;
  dob: string;
  genderCd: Gender;
  effectiveStartDt: string;
  effectiveEndDt?: string;
  groupId?: number;
  consultReimbursementMethodCode?: string;
  readOnly?: boolean;
}

export interface MemberUpdateRequest extends BaseMemberMetaData {
  memberMasterMrn?: string;
  sourceMrnCd?: string;
  salutation?: string;
  jobTitle?: string;
  ssn?: string;
  ethnicRace?: string;
  addresses?: Address[];
  phones?: Phone[];
  emails?: Email[];
  languagePreferences?: LanguagePreference[];
  relatedInstances?: RelatedInstance[];
}

export interface MemberCreateRequest extends BaseMemberMetaData {
  addresses?: Address[];
  phones?: Phone[];
  emails?: Email[];
  altInstances?: AltInstance[];
  relatedInstances?: RelatedInstance[];
  languagePreferences?: LanguagePreference[];
}

export interface LanguagePreference {
  languageCd: string;
}

export interface Address {
  addressTypeCd: AddressType;
  dataSourceCd?: string;
  preferredFlg: FlagMarker;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  city: string;
  stateProvince: string;
  postal: string;
}

export interface Phone {
  areaCode?: string;
  partyPhoneFaxId?: number;
  phoneFaxTypeCd: PhoneFaxType;
  dataSourceCd?: string;
  preferredFlg: FlagMarker;
  phoneNumber: string;
  extension?: string;
}

export interface Email {
  emailTypeCd: EmailType;
  dataSourceCd?: string;
  preferredFlg: FlagMarker;
  emailAddress: string;
}

export interface AltInstance {
  altCd: string;
  altId: string;
  dataSourceCd?: string;
}

export interface RelatedInstance {
  mrnId: string;
  relationshipTypeCd: RelationshipType;
  relationshipSubtypeCd: RelationshipSubType;
  relationshipStatusCd: RelationshipStatus;
  dataSourceCd?: string;
}

export interface ManageMemberResult {
  memberMasterMrn: string;
}

export interface DependentFormData {
  memberMasterMrnId?: string;
  relationshipSubtypeCd: RelationshipSubType;
  firstNm: string;
  middleNm?: string;
  lastNm: string;
  suffix?: string;
  dob: string;
  genderCd: Gender;
  languageCd: string;
  clientId: string;
  contactEmail?: string;
  effectiveStartDt: string;
}

export interface ManageMamberResult {
  address?: number[];
  altInstance?: number[];
  email?: number[];
  groupId?: number;
  languagePreferences?: number[];
  memberMasterMrnId: number;
  mrnSourceClassificationCd?: string;
  namespace?: number;
  relatedInstances?: number[];
  phone?: number[];
  sourceMrnCd: number;
  sourceMrnId: number;
  sourceMrnRoot?: string;
}

export interface ManageMemberResponse {
  errors?: string[];
  members: ManageMemberResult[];
}

export interface MemberDetails extends BaseMemberMetaData {
  memberMasterMrn: string;
  sourceMrnCd: string;
  createdAt: string;
  updatedAt?: string;
  ethnicRace?: string;
  exclusionCd?: string;
  jobTitle?: string;
  salutation?: string;
  ssn?: string;
  addresses?: Address[];
  group: Group;
  emails?: Email[];
  languagePreferences: LanguagePreference[];
  phones: Phone[];
  relatedInstances?: MemberRelatedInstance[];
  primaryId?: string;
}

export interface MemberRelatedInstance {
  mrnId: string;
  dob: string;
  firstNm: string;
  lastNm: string;
  middleNm?: string;
  genderCd: Gender;
  salutation?: string;
  suffix?: string;
  relationshipSubtypeCd: RelationshipSubType;
  relationshipStatusCd: RelationshipStatus;
  relationshipTypeCd: RelationshipType;
  personPolyTypeCd?: string;
  addresses?: Address[];
  emails?: Email[];
  languagePreferences?: LanguagePreference[];
  phones?: Phone[];
  createdAt?: string;
}

export type DependentMetaWithFromData = ManageMemberResult & DependentFormData;

export interface EligibilitySearchResponse {
  membersSearch: {
    eligibilityMembers: EligibilityMember[];
  };
}

export interface Group {
  id: number;
  name: string;
  primarySourceRegistration: string;
  dependentSourceRegistration: string;
  consultReimbursementMethodCode: string;
}

export interface EligibilityMember {
  mrnId: number;
  firstName: string;
  lastName: string;
  group: Group;
  primaryFlg: FlagMarker;
  createdAt: string;
  dateOfBirth: string;
  updatedAt: string;
  // all fields below this line are not available for the moment but required by member update endpoint
  sourceMrnCd?: string;
  effectiveEndDt?: string;
  effectiveStartDt?: string;
  sourceMrnRoot?: string;
  sourceMrnId?: string;
  dataSourceCd?: string;
  namespace?: string;
  mrnSourceClassificationCd?: string;
  genderCd?: Gender;
}

export interface Member {
  mrnId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  mrnCreatedAt: string;
  createdAt?: string;
  coverageStartDate: string;
  coverageEndDate?: string;
  group: Group;
  isPrimary: boolean;
  primaryMemberId?: number;
}

export interface DependentMember extends Member {
  effectiveStartDt: string;
  effectiveEndDt: string;
  dob: string;
}

type TypeInCell<T> = {
  row: { original: T; canExpand: boolean; isExpanded: boolean; getToggleRowExpandedProps(): any };
};
export type MemberInCell = TypeInCell<Member>;
export type DependentMemberInCell = TypeInCell<DependentMember>;

export enum GroupRegistrationCode {
  REGTYPE_OPEN = 'REGTYPE_OPEN',
  REGTYPE_STAGEDELIGIBILITY = 'REGTYPE_STAGEDELIGIBILITY',
  REGTYPE_STAGEDREGISTRATION = 'REGTYPE_STAGEDREGISTRATION',
  REGTYPE_STAGEDCLIENT = 'REGTYPE_STAGEDCLIENT',
  REGTYPE_RTE = 'REGTYPE_RTE',
  REGTYPE_HYBRID = 'REGTYPE_HYBRID',
  REGTYPE_APIHYBRID = 'REGTYPE_APIHYBRID'
}

export enum MemberToggleStatusAction {
  Reactivate = 'reactivate',
  Terminate = 'terminate'
}

export enum MemberSourceRegistrationCd {
  OPEN = 'OPEN',
  RTE_COMMERCIAL = 'RTE_COMMERCIAL',
  STAGE_COMMERCIAL = 'STAGE_COMMERCIAL',
  PORTAL_COMMERCIAL = 'PORTAL_COMMERCIAL',
  PORTAL_EXPERT = 'PORTAL_EXPERT',
  PARTNERAPI_COMMERCIAL = 'PARTNERAPI_COMMERCIAL',
  CLIENT_COMMERCIAL = 'CLIENT_COMMERCIAL',
  ELIGIBILITY = 'ELIGIBILITY'
}
